import type { ApolloError } from '@apollo/client';
import { Badge, BadgeColor, Button } from '@odin-labs/components';
import { modeLabels } from 'accessGroups/presentation';
import {
  ConnectorRunStatus,
  ConnectorStatus,
  ConnectorType,
  type GetSiteLayoutPublicationsQuery,
  type SiteLayoutPublicationFieldsFragment,
  ZonesMode,
} from 'apollo/generated/client-operations';
import moment from 'moment-timezone';
import React from 'react';

/** What one read says about delivery of the effective layout to Avigilon. */
export type PublicationRead =
  | { state: 'loading' }
  /** The read names only revisions other than the designer's effective one: both are read again. */
  | { state: 'stale'; revisionIds: string[] }
  | { state: 'failed' }
  | {
      state: 'loaded';
      zonesMode: ZonesMode;
      /** The site's Avigilon Access connector's status now; null while the site has none. */
      connectorStatus: ConnectorStatus | null;
      /** Null while delivery to Avigilon was never requested for the effective revision. */
      publication: SiteLayoutPublicationFieldsFragment | null;
    };

/**
 * The Avigilon Access connector's status and its delivery of the effective
 * revision, among the site's connectors and layout publications; other
 * connectors are not shown. Only a read of this site is used. Any error
 * fails the read, even over an earlier result, and so does a read that
 * finds no site; a publications list that could not be read is a failure,
 * not "never requested". A delivery of another revision is not this
 * revision's.
 */
export const publicationReadOf = (
  siteId: string,
  effectiveRevisionId: string | null,
  data: GetSiteLayoutPublicationsQuery | undefined,
  error: ApolloError | undefined,
): PublicationRead => {
  if (error || data?.getJobsite === null) return { state: 'failed' };
  const site =
    data?.getJobsite?.jobsiteId === siteId ? data.getJobsite : undefined;
  if (!site) return { state: 'loading' };
  if (!site.siteLayoutPublications) return { state: 'failed' };
  const avigilon = site.siteLayoutPublications.filter(
    ({ connectorType }) => connectorType === ConnectorType.AvigilonAccess,
  );
  const publication =
    avigilon.find(({ revisionId }) => revisionId === effectiveRevisionId) ??
    null;
  if (!publication && avigilon.length)
    return {
      state: 'stale',
      revisionIds: avigilon.map(({ revisionId }) => revisionId).sort(),
    };
  const connectorStatus =
    site.connectors.find(({ type }) => type === ConnectorType.AvigilonAccess)
      ?.status ?? null;
  return {
    state: 'loaded',
    zonesMode: site.zonesMode,
    connectorStatus,
    publication,
  };
};

/**
 * Delivery is still to happen: a next attempt is due, or a run is under way.
 * Never to a connector that is not enabled, which delivery writes nothing to.
 */
export const deliveryOutstanding = (read: PublicationRead): boolean =>
  read.state === 'loaded' &&
  read.connectorStatus === ConnectorStatus.Enabled &&
  !!read.publication &&
  (!!read.publication.nextAttemptAt ||
    read.publication.run?.status === ConnectorRunStatus.Running);

/** Outstanding delivery whose run is under way now. */
export const deliveryRunning = (read: PublicationRead): boolean =>
  deliveryOutstanding(read) &&
  read.state === 'loaded' &&
  read.publication?.run?.status === ConnectorRunStatus.Running;

// Delivery prefixes its run messages with the operation and revision:
// `SITE_LAYOUT_PUBLISHED revision <id>: <text>`. Only the text is shown.
const RUN_MESSAGE_PREFIX = /^SITE_LAYOUT_PUBLISHED revision [^:]*: /;
const runMessageText = (message: string): string =>
  message.replace(RUN_MESSAGE_PREFIX, '');

// Delivery stops counting attempts here (`SiteLayoutPublicationRequest`).
const MAX_COUNTED_ATTEMPTS = 30;

const at = (iso: string): string => moment(iso).format('MMM D, h:mm a');

export type AvigilonSyncStatusProps = {
  hasPublishedLayout: boolean;
  read: PublicationRead;
  /** A designer command is running, or one stalled. */
  busy: boolean;
  onSync: () => void;
};

const classes = {
  panel: 'odin-flex odin-flex-col odin-items-start odin-gap-2 odin-px-4',
  text: 'odin-text-xs odin-text-gray-700',
  muted: 'odin-text-xs odin-text-gray-500',
  failed: 'odin-text-xs odin-text-red-600',
};

/**
 * How delivery of the site's effective layout to Avigilon went. While the
 * site's Avigilon Access connector is not enabled, the panel says nothing is
 * synced to it, whatever its delivery: delivery writes nothing to it, a run
 * from before it was disabled is stale, and a revision published since has
 * no delivery to it at all. Otherwise a succeeded run is still not proof
 * anything was written: delivery also succeeds without writing while zones
 * are off, the connector does not support layouts, or the revision was
 * superseded. So it is shown as succeeded, never as synced, with the run's
 * own message saying what it did. Sync now requests delivery again; it is
 * unavailable while the Avigilon Access connector is not enabled, or a run
 * is under way.
 */
export function AvigilonSyncStatus({
  hasPublishedLayout,
  read,
  busy,
  onSync,
}: AvigilonSyncStatusProps): React.ReactElement {
  const notEnabled =
    read.state === 'loaded' &&
    read.connectorStatus !== null &&
    read.connectorStatus !== ConnectorStatus.Enabled;
  const run =
    read.state === 'loaded' && !notEnabled ? read.publication?.run : null;
  const running = run?.status === ConnectorRunStatus.Running;

  const noLayoutText =
    'No published layout yet. Zones and entries are created in Avigilon after a layout is published while zones are on.';
  // A read still loading, or one that failed, says so even while there is no
  // published layout: another admin may have published since.
  let body: React.ReactNode;
  if (read.state === 'loading' || read.state === 'stale') {
    body = <p className={classes.muted}>Loading the Avigilon sync status…</p>;
  } else if (read.state === 'failed') {
    body = (
      <p className={classes.failed}>Could not load the Avigilon sync status.</p>
    );
  } else {
    const { zonesMode, connectorStatus, publication } = read;
    const off = zonesMode === ZonesMode.Off;
    let badge: { text: string; color: BadgeColor } | null = null;
    const details: string[] = [];
    if (!hasPublishedLayout) {
      details.push(noLayoutText);
    } else if (notEnabled) {
      details.push(
        connectorStatus === ConnectorStatus.Disabled
          ? 'The Avigilon Access connector is disabled, so nothing is synced to it.'
          : 'The Avigilon Access connector is not enabled, so nothing is synced to it.',
      );
    } else if (!publication) {
      details.push('No sync requested for the current revision.');
    } else {
      const { nextAttemptAt, attempts } = publication;
      const pending = { text: 'Pending', color: 'yellow' as const };
      if (!run) {
        if (nextAttemptAt) badge = pending;
        details.push('Not synced yet.');
        if (nextAttemptAt) details.push(`Next attempt ${at(nextAttemptAt)}.`);
      } else if (run.status === ConnectorRunStatus.Running) {
        badge = { text: 'Running', color: 'gray' };
        details.push(`Started ${at(run.startedAt)}.`);
      } else if (run.status === ConnectorRunStatus.Failed) {
        badge = { text: 'Failed', color: 'red' };
        details.push(`Last run ${at(run.finishedAt ?? run.startedAt)}.`);
        // `attempts` counts the attempts since delivery last succeeded, up to
        // a cap; the next is one more, or past the cap.
        if (nextAttemptAt)
          details.push(
            `Retrying ${at(nextAttemptAt)} (attempt ${
              attempts >= MAX_COUNTED_ATTEMPTS
                ? `${MAX_COUNTED_ATTEMPTS + 1} or later`
                : attempts + 1
            }).`,
          );
      } else if (run.status === ConnectorRunStatus.Succeeded) {
        // Whether it wrote anything is the run message's to say, with the
        // zones mode below.
        badge = nextAttemptAt ? pending : { text: 'Succeeded', color: 'gray' };
        details.push(`Last run ${at(run.finishedAt ?? run.startedAt)}.`);
        if (nextAttemptAt) details.push(`Next attempt ${at(nextAttemptAt)}.`);
      }
      if (run?.message) details.push(runMessageText(run.message));
    }
    body = (
      <>
        {badge && <Badge text={badge.text} color={badge.color} />}
        {details.map((detail) => (
          <p key={detail} className={classes.text}>
            {detail}
          </p>
        ))}
        <p className={classes.muted}>
          {off
            ? 'Zones are off for this site. Nothing is written to Avigilon until zones are enabled.'
            : `Zones mode: ${modeLabels[zonesMode]}`}
        </p>
      </>
    );
  }

  return (
    <div className={classes.panel}>
      {body}
      <Button
        text="Sync now"
        size="xs"
        theme="white"
        disabled={!hasPublishedLayout || busy || running || notEnabled}
        onClick={onSync}
      />
    </div>
  );
}
